import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../../../store';
import {
  fetchCreateScheduleHours,
  fetchActualHours,
  fetchShiftTimes,
  fetchEmployeeRoles,
  fetchAllEmployeeRoles,
  fetchEmployees,
  fetchScheduleEmployeeData,
  saveScheduleData,
  submitScheduleStatus
} from '../../../../../store/thunks/dloThunks';
import {
  selectCreateScheduleHours,
  selectCreateScheduleHoursLoading,
  selectCreateScheduleHoursError,
  selectActualHours,
  selectActualHoursLoading,
  selectActualHoursError,
  selectCreateSchedulePeriod,
  selectCreateScheduleWeek,
  selectCreateScheduleYear,
  selectCreateScheduleEmployeeRows,
  selectCreateScheduleDeletedEmployeeRows,
  selectFilteredRoles,
  selectEmployeeRoles,
  selectAllEmployeeRoles,
  selectEmployeeRolesLoading,
  selectEmployeeRolesError,
  selectEmployees,
  selectEmployeesLoading,
  selectEmployeesError,
  selectScheduleEmployeeData,
  selectScheduleEmployeeDataLoading,
  selectScheduleEmployeeDataError,
  selectInitialSchedules,
  selectSaveLoading,
  selectSaveError,
  selectSaveSuccess,
  selectHasUnsavedChanges,
  selectSubmitLoading,
  selectSubmitError,
  selectSubmitSuccess,
  selectSubmitResponse,
  selectSelectedDays,
  selectDayFilter,
  selectCreateActualHoursEmployeeRows,
  selectCreateActualHoursDeletedEmployeeRows,
  selectActualFilteredRoles,
  selectActualSelectedDays,
  selectActualDayFilter,
  setCreateSchedulePeriodWeek,
  addEmployeeRow,
  updateEmployeeRow,
  removeEmployeeRow,
  clearAllEmployeeRows,
  clearScheduleEmployeeData,
  clearAllActualEmployeeRows,
  updateScheduleHours,
  updateActualHours,
  setFilteredRoles,
  toggleSelectedDay,
  clearSelectedDays,
  setDayFilter,
  splitEmployeeRole,
  splitRoleAfterSibling,
  removeSplitRole,
  resetSaveState,
  addActualEmployeeRow,
  updateActualEmployeeRow,
  removeActualEmployeeRow,
  setActualFilteredRoles,
  toggleActualSelectedDay,
  clearActualSelectedDays,
  setActualDayFilter,
  splitActualEmployeeRole,
  splitActualRoleAfterSibling,
  removeActualSplitRole,
  copyScheduleToActualHours
} from '../../../../../store/slices/dloSlice';
import { EmployeeRowData } from '../types';
import {
  transformScheduleApiToEmployeeRows,
  transformScheduleApiToActualEmployeeRows,
  getEmployeeOptions,
  getRoleOptions,
  getShiftOptions
} from '../utils/scheduleHelpers';

/**
 * Main custom hook for DLO Create Schedule functionality
 */
export const useCreateSchedule = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Tracks whether schedule-tab rows have already been seeded from API data.
  // Prevents employeeRows.length changes (caused by addEmployeeRow dispatches
  // inside the effect) from immediately re-triggering the same effect and
  // creating an addRow → length-change → effect-run loop (React error #185).
  const scheduleRowsInitializedRef = useRef(false);

  // Same guard for the Actual Hours tab population effect.
  const actualRowsInitializedRef = useRef(false);

  const getEmployeeRoleShiftKey = useCallback((row: any) => {
    const employeeKey = row?.employee?.label || row?.employee?.value || '';
    const roleKey = row?.role?.label || row?.role?.value || '';
    const shiftKey = row?.shift?.label || row?.shift?.value || '';
    return `${employeeKey}-${roleKey}-${shiftKey}`;
  }, []);

  // Get period and week data from Redux
  const appData = useSelector((state: RootState) => state.appData);
  const periodWeekData = appData.periodWeek?.data;

  // Get DLO create schedule data from Redux
  const scheduleHours = useSelector(selectCreateScheduleHours);
  const scheduleHoursLoading = useSelector(selectCreateScheduleHoursLoading);
  const scheduleHoursError = useSelector(selectCreateScheduleHoursError);
  const actualHours = useSelector(selectActualHours);
  const actualHoursLoading = useSelector(selectActualHoursLoading);
  const actualHoursError = useSelector(selectActualHoursError);
  const createSchedulePeriod = useSelector(selectCreateSchedulePeriod);
  const createScheduleWeek = useSelector(selectCreateScheduleWeek);
  const createScheduleYear = useSelector(selectCreateScheduleYear);
  const employeeRows = useSelector(selectCreateScheduleEmployeeRows);
  const deletedEmployeeRows = useSelector(selectCreateScheduleDeletedEmployeeRows);
  const filteredRoles = useSelector(selectFilteredRoles);
  const employeeRoles = useSelector(selectEmployeeRoles);
  const allEmployeeRoles = useSelector(selectAllEmployeeRoles);
  const employeeRolesLoading = useSelector(selectEmployeeRolesLoading);
  const employeeRolesError = useSelector(selectEmployeeRolesError);
  const employees = useSelector(selectEmployees);
  const employeesLoading = useSelector(selectEmployeesLoading);
  const employeesError = useSelector(selectEmployeesError);
  const scheduleEmployeeData = useSelector(selectScheduleEmployeeData);
  const scheduleEmployeeDataLoading = useSelector(selectScheduleEmployeeDataLoading);
  const scheduleEmployeeDataError = useSelector(selectScheduleEmployeeDataError);
  const initialSchedules = useSelector(selectInitialSchedules);
  const saveLoading = useSelector(selectSaveLoading);
  const saveError = useSelector(selectSaveError);
  const saveSuccess = useSelector(selectSaveSuccess);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const submitLoading = useSelector(selectSubmitLoading);
  const submitError = useSelector(selectSubmitError);
  const submitSuccess = useSelector(selectSubmitSuccess);
  const submitResponse = useSelector(selectSubmitResponse);
  const selectedDays = useSelector(selectSelectedDays);
  const dayFilter = useSelector(selectDayFilter);
  const shiftTimes = useSelector((state: RootState) => state.dlo.locationSetting.shiftTimes);
  const shiftTimesLoading = useSelector((state: RootState) => state.dlo.locationSetting.shiftTimesLoading);

  // Get Actual Hours data from Redux
  const actualEmployeeRows = useSelector(selectCreateActualHoursEmployeeRows);
  const actualDeletedEmployeeRows = useSelector(selectCreateActualHoursDeletedEmployeeRows);
  const actualFilteredRoles = useSelector(selectActualFilteredRoles);
  const actualSelectedDays = useSelector(selectActualSelectedDays);
  const actualDayFilter = useSelector(selectActualDayFilter);

    /**
   * API Integration: Fetch Create Schedule Hours and Employee Data
   * This useEffect handles all data fetching when period, week, or location changes
   */
  useEffect(() => {
    console.log('=== Data Fetch Effect Triggered ===');
    console.log('Current state:', {
      periodWeekData,
      createSchedulePeriod,
      createScheduleWeek,
      locationId: appData?.selectedLocation?.id
    });

    // Set period, week, and year in Redux if they don't exist
    if (periodWeekData?.period && periodWeekData?.week && !createSchedulePeriod) {
      const currentYear = new Date().getFullYear();
      console.log('Setting create schedule period/week/year from app data:', {
        period: periodWeekData.period,
        week: periodWeekData.week,
        year: currentYear
      });
      dispatch(setCreateSchedulePeriodWeek({
        period: periodWeekData.period,
        week: periodWeekData.week,
        year: currentYear
      }));
    }

    // Clear existing employee data before fetching new data to prevent stale data issues
    if (createSchedulePeriod && createScheduleWeek) {
      console.log('Clearing existing employee data before fetching fresh data for period/week change');
      dispatch(clearAllEmployeeRows());
      dispatch(clearAllActualEmployeeRows());
    }

    // Fetch employee roles on page load if location is available
    if (appData?.selectedLocation?.id) {
      console.log('Fetching employee roles');
      dispatch(fetchEmployeeRoles());
      // Also fetch all LO roles (incl. disabled) for existing schedule edge case
      dispatch(fetchAllEmployeeRoles());
    }

    // Fetch employees on page load if location is available
    if (appData?.selectedLocation?.id) {
      console.log('Fetching employees for location:', appData.selectedLocation.id);
      dispatch(fetchEmployees({ bypassCache: true }));
    }

    // Fetch shift times for the location
    if (appData?.selectedLocation?.id) {
      console.log('Fetching shift times for location:', appData.selectedLocation.id);
      dispatch(fetchShiftTimes());
    }

    // Fetch schedule hours data if period is available
    if (periodWeekData?.period || createSchedulePeriod) {
      const period = createSchedulePeriod || periodWeekData?.period || 0;
      const calculatedWeek = (period - 1) * 4 + 1;
      console.log('Fetching schedule hours with:', {
        period,
        calculatedWeek,
        location: appData?.selectedLocation?.id
      });
      dispatch(fetchCreateScheduleHours());
    }

    // Fetch schedule employee data if period, week, and location are available
    if ((periodWeekData?.period || createSchedulePeriod) && 
        (periodWeekData?.week || createScheduleWeek) && 
        appData?.selectedLocation?.id) {
      const period = createSchedulePeriod || periodWeekData?.period || 0;
      const week = createScheduleWeek || periodWeekData?.week || 0;
      const calculatedWeek = (period - 1) * 4 + week;
      const currentYear = createScheduleYear || new Date().getFullYear();
      console.log('Fetching schedule employee data with:', {
        period,
        week,
        calculatedWeek,
        year: currentYear,
        location: appData?.selectedLocation?.id
      });
      dispatch(fetchScheduleEmployeeData());
    }
  }, [dispatch, periodWeekData?.period, periodWeekData?.week, createSchedulePeriod, createScheduleWeek, appData?.selectedLocation?.id]);

  /**
   * Process Schedule Employee Data and Populate Employee Rows
   * This effect handles both initial load and updates after save operations
   */
  useEffect(() => {
    if (scheduleEmployeeData && 
        scheduleEmployeeData.data && 
        scheduleEmployeeData.data.length > 0 && 
        employees && 
        employees.length > 0 &&
        employeeRoles && 
        (Array.isArray(shiftTimes) || shiftTimes?.data)) {
      
      console.log('Processing schedule employee data:', scheduleEmployeeData);

      // Transform data to dropdown options
      const employeeOptions = getEmployeeOptions(employees);
      // For existing schedule rows use allEmployeeRoles so disabled roles still resolve
      const roleOptions = getRoleOptions(allEmployeeRoles?.data ? allEmployeeRoles : employeeRoles);
      const shiftOptions = getShiftOptions(shiftTimes);

      // Build a set of currently-active role IDs for invalid-role detection
      const activeRoleOptions = getRoleOptions(employeeRoles);
      const activeRoleIds = new Set(activeRoleOptions.map((r: any) => r.id));

      // Transform API data to employee rows
      const rawTransformedRows = transformScheduleApiToEmployeeRows(
        scheduleEmployeeData.data,
        employeeOptions,
        roleOptions,
        shiftOptions
      );

      // Mark rows whose role is no longer active (disabled/removed) so the UI can flag them
      const transformedRows = rawTransformedRows.map((row: EmployeeRowData) => ({
        ...row,
        isRoleInvalid: !!(row.role && !activeRoleIds.has(row.role.id)),
      }));

      console.log('Transformed employee rows:', transformedRows);

      // If we have no rows yet, initialize them (initial load)
      if (employeeRows.length === 0 && !scheduleRowsInitializedRef.current) {
        scheduleRowsInitializedRef.current = true;
        console.log('Initial load: Creating schedule employee rows from API data');
        
        // Add empty rows for each transformed row
        transformedRows.forEach(() => {
          dispatch(addEmployeeRow());
        });

        // Use setTimeout to ensure rows are created before updating them
        setTimeout(() => {
          transformedRows.forEach((transformedRow: EmployeeRowData, index: number) => {
            const rowId = (index + 1).toString(); // Row IDs start from 1

            const updates = {
              employee: transformedRow.employee,
              role: transformedRow.role,
              shift: transformedRow.shift,
              sunday: transformedRow.sunday,
              monday: transformedRow.monday,
              tuesday: transformedRow.tuesday,
              wednesday: transformedRow.wednesday,
              thursday: transformedRow.thursday,
              friday: transformedRow.friday,
              saturday: transformedRow.saturday,
              total: transformedRow.total,
              isSplit: transformedRow.isSplit,
              isChildRow: transformedRow.isChildRow,
              parentId: transformedRow.parentId,
              isRoleInvalid: transformedRow.isRoleInvalid,
              sourceSnapshot: transformedRow.sourceSnapshot,
            };

            console.log(`Updating row ${rowId} with:`, updates);
            dispatch(updateEmployeeRow({ id: rowId, updates, isSystemUpdate: true }));
          });
        }, 100); // Small delay to ensure rows are created

        console.log(`Completed processing ${transformedRows.length} employee rows`);
      } else {
        // If rows exist, update them with fresh data from API (after save operation)
        console.log('Updating existing schedule employee rows with fresh API data');
        
        // Create a map of existing rows by employee-role-shift for easy lookup
        const existingRowsMap = new Map<string, any>();
        employeeRows.forEach((row: any) => {
          const key = `${row.employee?.label || row.employee?.value}-${row.role?.label || row.role?.value}-${row.shift?.label || row.shift?.value}`;
          existingRowsMap.set(key, row);
        });

        // Update existing rows with new values from API
        transformedRows.forEach((transformedRow: EmployeeRowData) => {
          const key = `${transformedRow.employee?.label || transformedRow.employee?.value}-${transformedRow.role?.label || transformedRow.role?.value}-${transformedRow.shift?.label || transformedRow.shift?.value}`;
          const existingRow = existingRowsMap.get(key);

          if (existingRow) {
            // Update the matching existing row with fresh API data
            const updates = {
              sunday: transformedRow.sunday,
              monday: transformedRow.monday,
              tuesday: transformedRow.tuesday,
              wednesday: transformedRow.wednesday,
              thursday: transformedRow.thursday,
              friday: transformedRow.friday,
              saturday: transformedRow.saturday,
              total: transformedRow.total,
              sourceSnapshot: transformedRow.sourceSnapshot,
            };

            console.log(`Updating existing schedule row ${existingRow.id} with fresh data:`, updates);
            dispatch(updateEmployeeRow({ id: existingRow.id, updates, isSystemUpdate: true }));
          }
        });

        console.log('Completed updating schedule employee rows with fresh data');
      }
    }
    }, [scheduleEmployeeData, employees, employeeRoles, allEmployeeRoles, shiftTimes, dispatch]);

  /**
   * Process Schedule Employee Data for Actual Hours Tab
   * This effect handles both initial load and updates after save operations
   */
  useEffect(() => {
    if (scheduleEmployeeData && 
        scheduleEmployeeData.data && 
        scheduleEmployeeData.data.length > 0 && 
        employees && 
        employees.length > 0 &&
        employeeRoles && 
        (Array.isArray(shiftTimes) || shiftTimes?.data)) {
      
      console.log('Processing schedule employee data for Actual Hours tab:', scheduleEmployeeData);

      // Transform data to dropdown options
      const employeeOptions = getEmployeeOptions(employees);
      // For existing schedule rows use allEmployeeRoles so disabled roles still resolve
      const roleOptions = getRoleOptions(allEmployeeRoles?.data ? allEmployeeRoles : employeeRoles);
      const shiftOptions = getShiftOptions(shiftTimes);

      // Build a set of currently-active role IDs for invalid-role detection
      const activeRoleOptions = getRoleOptions(employeeRoles);
      const activeRoleIds = new Set(activeRoleOptions.map((r: any) => r.id));

      // Transform API data to actual hours employee rows
      const rawTransformedRows = transformScheduleApiToActualEmployeeRows(
        scheduleEmployeeData.data,
        employeeOptions,
        roleOptions,
        shiftOptions
      );

      // Mark rows whose role is no longer active (disabled/removed) so the UI can flag them
      const transformedRows = rawTransformedRows.map((row: EmployeeRowData) => ({
        ...row,
        isRoleInvalid: !!(row.role && !activeRoleIds.has(row.role.id)),
      }));

      console.log('Transformed actual hours employee rows:', transformedRows);

      // If we have no rows yet, initialize them (initial load)
      if (actualEmployeeRows.length === 0 && !actualRowsInitializedRef.current) {
        actualRowsInitializedRef.current = true;
        console.log('Initial load: Creating actual employee rows from API data');
        
        // Add empty actual rows for each transformed row
        transformedRows.forEach(() => {
          dispatch(addActualEmployeeRow());
        });

        // Use setTimeout to ensure rows are created before updating them
        setTimeout(() => {
          transformedRows.forEach((transformedRow: EmployeeRowData, index: number) => {
            const rowId = (index + 1).toString(); // Row IDs start from 1

            const updates = {
              employee: transformedRow.employee,
              role: transformedRow.role,
              shift: transformedRow.shift,
              sunday: transformedRow.sunday,
              monday: transformedRow.monday,
              tuesday: transformedRow.tuesday,
              wednesday: transformedRow.wednesday,
              thursday: transformedRow.thursday,
              friday: transformedRow.friday,
              saturday: transformedRow.saturday,
              total: transformedRow.total,
              isSplit: transformedRow.isSplit,
              isChildRow: transformedRow.isChildRow,
              parentId: transformedRow.parentId,
              isRoleInvalid: transformedRow.isRoleInvalid,
              sourceSnapshot: transformedRow.sourceSnapshot,
            };

            console.log(`Updating actual row ${rowId} with:`, updates);
            dispatch(updateActualEmployeeRow({ id: rowId, updates, isSystemUpdate: true }));
          });
        }, 100); // Small delay to ensure rows are created

        console.log(`Completed processing ${transformedRows.length} actual hours employee rows`);
      } else {
        // If rows exist, update them with fresh data from API (after save operation)
        console.log('Updating existing actual employee rows with fresh API data');

        const existingMeaningfulRows = actualEmployeeRows.filter(
          (row: any) => row?.employee && row?.role && row?.shift
        );
        const existingKeys = new Set(existingMeaningfulRows.map(getEmployeeRoleShiftKey));
        const transformedKeys = new Set(transformedRows.map(getEmployeeRoleShiftKey));
        const hasMissingRows = transformedRows.some(
          (row: any) => !existingKeys.has(getEmployeeRoleShiftKey(row))
        );
        const hasExtraRows = existingMeaningfulRows.some(
          (row: any) => !transformedKeys.has(getEmployeeRoleShiftKey(row))
        );

        // If a user adds a new employee in the Schedule tab and saves,
        // `actualEmployeeRows` will NOT contain that row yet. We must add/rebuild.
        if (hasMissingRows || hasExtraRows) {
          console.log(
            'Actual rows out of sync with API (missing/extra detected). Rebuilding actual rows from API.'
          );
          dispatch(clearAllActualEmployeeRows());

          transformedRows.forEach(() => {
            dispatch(addActualEmployeeRow());
          });

          setTimeout(() => {
            transformedRows.forEach((transformedRow: EmployeeRowData, index: number) => {
              const rowId = (index + 1).toString();

              const updates = {
                employee: transformedRow.employee,
                role: transformedRow.role,
                shift: transformedRow.shift,
                sunday: transformedRow.sunday,
                monday: transformedRow.monday,
                tuesday: transformedRow.tuesday,
                wednesday: transformedRow.wednesday,
                thursday: transformedRow.thursday,
                friday: transformedRow.friday,
                saturday: transformedRow.saturday,
                total: transformedRow.total,
                isSplit: transformedRow.isSplit,
                isChildRow: transformedRow.isChildRow,
                parentId: transformedRow.parentId,
                isRoleInvalid: transformedRow.isRoleInvalid,
                sourceSnapshot: transformedRow.sourceSnapshot,
              };

              dispatch(updateActualEmployeeRow({ id: rowId, updates, isSystemUpdate: true }));
            });
          }, 100);

          return;
        }
        
        // Create a map of existing rows by employee-role-shift for easy lookup
        const existingRowsMap = new Map<string, any>();
        actualEmployeeRows.forEach((row: any) => {
          const key = getEmployeeRoleShiftKey(row);
          existingRowsMap.set(key, row);
        });

        // Update existing rows with new values from API
        transformedRows.forEach((transformedRow: EmployeeRowData) => {
          const key = getEmployeeRoleShiftKey(transformedRow);
          const existingRow = existingRowsMap.get(key);

          if (existingRow) {
            // Update the matching existing row with fresh API data
            const updates = {
              sunday: transformedRow.sunday,
              monday: transformedRow.monday,
              tuesday: transformedRow.tuesday,
              wednesday: transformedRow.wednesday,
              thursday: transformedRow.thursday,
              friday: transformedRow.friday,
              saturday: transformedRow.saturday,
              total: transformedRow.total,
              sourceSnapshot: transformedRow.sourceSnapshot,
            };

            console.log(`Updating existing actual row ${existingRow.id} with fresh data:`, updates);
            dispatch(updateActualEmployeeRow({ id: existingRow.id, updates, isSystemUpdate: true }));
          }
        });

        console.log('Completed updating actual hours employee rows with fresh data');
      }
    }
  }, [scheduleEmployeeData, employees, employeeRoles, shiftTimes, dispatch, getEmployeeRoleShiftKey]);

  // Function to reset all create schedule state
  const resetCreateScheduleState = useCallback(() => {
    console.log('Resetting all Create Schedule state...');
    
    // Clear all employee rows and related data
    dispatch(clearAllEmployeeRows());
    dispatch(clearAllActualEmployeeRows());

    // Reset init guards so rows are re-seeded on the next navigation to this page
    scheduleRowsInitializedRef.current = false;
    actualRowsInitializedRef.current = false;
    
    // Clear the underlying schedule employee data that feeds the rows
    dispatch(clearScheduleEmployeeData());
    
    // Reset save/submit states
    dispatch(resetSaveState());
    
    // Clear selected days
    dispatch(clearSelectedDays());
    dispatch(clearActualSelectedDays());
    
    // Reset filtered roles to initial state
    dispatch(setFilteredRoles({
      DSD: [],
      'NON-DSD': [],
      Retail: [],
      Other: [],
    }));
    dispatch(setActualFilteredRoles({
      DSD: [],
      'NON-DSD': [],
      Retail: [],
      Other: [],
    }));
    
    // Clear period/week to force fresh data fetch
    // Note: We don't clear the period/week here as it's needed for navigation
    // The data will be refetched automatically when the useEffect runs
    
    console.log('Create Schedule state reset complete - all cached employee data cleared');
  }, [dispatch]);

  return {
    // Data
    scheduleHours,
    scheduleHoursLoading,
    scheduleHoursError,
    actualHours,
    actualHoursLoading,
    actualHoursError,
    createSchedulePeriod,
    createScheduleWeek,
    createScheduleYear,
    appData, // Expose appData for week calculations
    employeeRows,
    deletedEmployeeRows,
    filteredRoles,
    employeeRoles,
    employeeRolesLoading,
    employeeRolesError,
    employees,
    employeesLoading,
    employeesError,
    scheduleEmployeeData,
    scheduleEmployeeDataLoading,
    scheduleEmployeeDataError,
    initialSchedules,
    saveLoading,
    saveError,
    saveSuccess,
    hasUnsavedChanges,
    submitLoading,
    submitError,
    submitSuccess,
    submitResponse,
    selectedDays,
    dayFilter,
    shiftTimes,
    shiftTimesLoading,
    actualEmployeeRows,
    actualDeletedEmployeeRows,
    actualFilteredRoles,
    actualSelectedDays,
    actualDayFilter,

    // Actions
    dispatch,
    resetSaveState,
    setFilteredRoles,
    setActualFilteredRoles,
    setDayFilter,
    clearSelectedDays,
    setActualDayFilter,
    clearActualSelectedDays,
    addEmployeeRow,
    updateEmployeeRow,
    removeEmployeeRow,
    updateScheduleHours,
    updateActualHours,
    addActualEmployeeRow,
    updateActualEmployeeRow,
    removeActualEmployeeRow,
    splitEmployeeRole,
    splitRoleAfterSibling,
    removeSplitRole,
    splitActualEmployeeRole,
    splitActualRoleAfterSibling,
    removeActualSplitRole,
    toggleSelectedDay,
    toggleActualSelectedDay,
    saveScheduleData,
    submitScheduleStatus,
    resetCreateScheduleState
  };
};